import { Download, Eye, ImagesIcon, Plus, Trash2 } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { ClipLoader } from "react-spinners";
import { motion } from "framer-motion";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import {
  deleteUserLogos,
  fetchUserLogos,
  selectFetchedUserLogos,
  selectLogoFetchError,
  selectUserLogoLoaded,
  selectUserLogoLoading,
} from "store/slices/logosSlice";
import LogoUploadeModel from "shared/LogoUploadModal/LogoUploadModal";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import infiniteLoader from "../../assets/loaders/InfinityLoader.gif";
import ErrorFallback from "shared/ErrorFallback/ErrorFallback";
import ConfirmDialog from "shared/Dialog/ConfirmDialog ";
import toast from "react-hot-toast";
import { extractErrorMessage } from "utils/ErrorHandlers";

const LogoManagement = () => {
  const dispatch = useAppDispatch();
  const userLogos = useAppSelector(selectFetchedUserLogos);
  const error = useAppSelector(selectLogoFetchError);
  const [logoModalVisible, setLogoModalVisible] = useState(false);
  const [logoUploadType, setLogoUploadType] = useState("upload");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoName, setLogoName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState<any>();
  const [logoError, setLogoError] = useState("");

  const isLogosLoaded = useAppSelector(selectUserLogoLoaded);
  const isLogosLoading = useAppSelector(selectUserLogoLoading);
  const [loadingLogoAction, setLoadingLogoAction] = useState<{
    [logoId: string]: "view" | "download" | "delete" | null;
  }>({});
  const [showDialog, setShowDialog] = useState(false);
  const [logoId, setLogoId] = useState("");
  const [logoDeleting, setLogoDeleting] = useState(false);
  const [logoChanged, setLogoChanged] = useState(false);

  const handleFetchLogos = useCallback(() => {
    dispatch(fetchUserLogos());
  }, [dispatch]);

  useEffect(() => {
    handleFetchLogos();
  }, [logoChanged, handleFetchLogos]);

  const handleAddLogo = () => {
    setLogoModalVisible(true);
    setLogoUploadType("upload");
    setLogoUrl("");
    setLogoName("");
    setLogoFile(null);
    setLogoPreview(null);
    setLogoError("");
  };

  const closeAddLogoDialogue = () => {
    setLogoModalVisible(false);
  };

  const viewLogo = async (index: number) => {
    const logoId = userLogos[index].id;
    setLoadingLogoAction((prev) => ({ ...prev, [logoId]: "view" }));
    try {
      window.open(userLogos[index].logo, "_blank");
    } finally {
      setLoadingLogoAction((prev) => ({ ...prev, [logoId]: null }));
    }
  };

  const downloadLogo = async (index: number) => {
    const { id, logo, name } = userLogos[index];
    setLoadingLogoAction((prev: any) => ({ ...prev, [id]: "download" }));

    try {
      const response = await fetch(logo, { mode: "cors" });
      if (!response.ok) throw new Error("Failed to fetch the logo file");

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;

      // Infer extension if possible (e.g., .png, .svg), fallback to .png
      const extension = logo.split(".").pop()?.split("?")[0] || "png";
      link.download = `${name || "logo"}.${extension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download logo:", error);
      const message = extractErrorMessage(error);
      toast.error(message || "Error downloading logo. Please try again");
    } finally {
      setLoadingLogoAction((prev) => ({ ...prev, [id]: null }));
    }
  };

  const handleDeleteLogo = async (logoId: string) => {
    setLogoDeleting(true);
    setLoadingLogoAction((prev) => ({ ...prev, [logoId]: "delete" }));
    try {
      await dispatch(deleteUserLogos([logoId])).unwrap();
      setLogoDeleting(false);
      setLogoChanged((prev) => !prev);
      setShowDialog(false);
    } catch (error: any) {
      setLogoDeleting(false);
      setShowDialog(false);
    } finally {
      setLoadingLogoAction((prev) => ({ ...prev, [logoId]: null }));
    }
  };

  return (
    <>
      {/* Logo Management Section */}
      <div className=" bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-2xl p-2 md:p-8">
        <div className="md:flex justify-between items-center mb-6 text-center md:text-left">
          <h2 className="text-2xl font-bold mb-4 mt-5 font-inter text-secondary-gradient">
            Logo Collection
          </h2>
          <ButtonPrimary
            onClick={handleAddLogo}
            className="px-4 py-2 bg-gradient-to-r w-auto bg-secondary-gradient text-white font-medium shadow-lg flex items-center gap-2 font-inter"
          >
            <Plus size={18} />
            Add Logo
          </ButtonPrimary>
        </div>

        {isLogosLoading && !isLogosLoaded ? (
          <div className="flex justify-center items-center">
            <img src={infiniteLoader} alt="loader" className="w-[12%]" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl py-4 px-2">
            {error ? (
              <ErrorFallback
                message={error}
                className="min-h-fit"
                onRetry={handleFetchLogos}
              />
            ) : userLogos.length <= 0 ? (
              <div className="text-center py-12">
                <ImagesIcon size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-inter">
                  No logos uploaded yet
                </p>
                <p className="text-gray-400 text-sm font-inter">
                  Start by adding your first logo
                </p>
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto">
                {userLogos.map((logo: any, index: any) => (
                  <motion.div
                    key={logo.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center group flex-shrink-0 relative bg-gray-200/10 backdrop-blur-sm rounded-2xl border border-gray-200/20 hover:shadow-lg transition-all duration-300 w-[160px]"
                  >
                    <div className="aspect-square p-4 checkerboard rounded-t-2xl">
                      <img
                        src={logo.logo}
                        alt={logo.id}
                        className="w-14 h-14 object-contain rounded-xl mx-auto"
                      />
                    </div>

                    <div className="p-4 pt-0">
                      <h3 className="font-semibold text-gray-800 truncate mb-1 font-inter">
                        {logo.name}
                      </h3>
                      <p className="text-xs text-gray-500 mb-2">
                        {/* {logo.type === "upload" ? "Uploaded" : "URL"} •{" "} */}
                        {/* {logo.createdAt} */}
                      </p>
                    </div>

                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-2 z-10 rounded-2xl">
                      {["view", "download", "delete"].includes(
                        loadingLogoAction[logo.id] ?? ""
                      ) ? (
                        <ClipLoader loading={true} size={24} color="#1d4ed8" />
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => viewLogo(index)}
                            className="p-2 bg-white/20 rounded-lg text-white hover:bg-white/30 transition-colors duration-200"
                            title="View Full Size"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => downloadLogo(index)}
                            className="p-2 bg-white/20 rounded-lg text-white hover:bg-white/30 transition-colors duration-200"
                            title="Download"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setShowDialog(true);
                              setLogoId(logo.id);
                            }}
                            className="p-2 bg-red-500/80 rounded-lg text-white hover:bg-red-600/80 transition-colors duration-200"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {logoModalVisible && (
        <LogoUploadeModel
          onClose={closeAddLogoDialogue}
          setLogoChanged={setLogoChanged}
        />
      )}
      <ConfirmDialog
        isOpen={showDialog}
        loading={logoDeleting}
        onClose={() => setShowDialog(false)}
        onConfirm={() => handleDeleteLogo(logoId)}
        title="Delete Logo"
        message="Are you sure you want to delete the logo? This action cannot be undone."
      />
    </>
  );
};

export default LogoManagement;
